import { useState, useEffect, useRef } from 'react';
import { useSocket } from '../../context/SocketContext';
import EmojiPicker from './EmojiPicker';
import useVisio from '../../hooks/useVisio';
import BoutonAppelVideo from './BoutonAppelVideo';

function timeAgo(date) {
  const diff = (Date.now() - new Date(date)) / 1000;
  if (diff < 60) return "A l'instant";
  if (diff < 3600) return Math.floor(diff/60) + ' min';
  if (diff < 86400) return Math.floor(diff/3600) + 'h';
  const d = new Date(date);
  return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
}

export default function Messagerie({ token, currentUser }) {
  const [convs, setConvs] = useState([]);
  const [activeConv, setActiveConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const fileRef = useRef();
  const bottomRef = useRef();
  const signalRef = useRef(null);
  const [reportedConvs, setReportedConvs] = useState(() => { try { return JSON.parse(localStorage.getItem('mjreportedconv') || '[]'); } catch { return []; } });

  const getOther = (conv) => conv?.participants?.find(p => p._id !== currentUser?._id && p._id !== currentUser?.id);
  const otherActive = getOther(activeConv);

  useEffect(() => {
    const id = otherActive?._id;
    if (!id) return;
    if (!window.__mjOpenChats) window.__mjOpenChats = new Set();
    window.__mjOpenChats.add(String(id));
    return () => { window.__mjOpenChats && window.__mjOpenChats.delete(String(id)); };
  }, [otherActive?._id]);
  const visio = useVisio(otherActive?._id, currentUser?.username || 'Anonyme');

  const globalSocketRef = useSocket();
  useEffect(() => { signalRef.current = globalSocketRef?.current; }, [globalSocketRef]);

  useEffect(() => { fetchConvs(); }, []);

  useEffect(() => {
    if (activeConv) fetchMessages(activeConv._id || activeConv.id);
  }, [activeConv]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    return () => { visio.stopVisio(); };
  }, [activeConv]);

  const handleSignalerConv = async () => {
    const convId = activeConv?._id || activeConv?.id;
    if (!convId || !otherActive?._id) return;
    if (reportedConvs.includes(String(convId))) return;
    if (!window.confirm('Signaler cette discussion ?')) return;
    const snapshot = (messages || []).slice(-10).map(m => ({
      from: (m.sender && (m.sender._id === currentUser?._id || m.sender._id === currentUser?.id)) ? (currentUser?.username || 'Moi') : (m.sender?.username || otherActive?.username || 'Membre'),
      text: m.text || '',
      image: m.imageUrl || '',
      at: m.createdAt || null
    }));
    try {
      const res = await fetch('/api/reports', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
        body: JSON.stringify({ targetType: 'discussion', targetId: String(convId), targetUserId: otherActive._id, targetLabel: 'Discussion avec ' + (otherActive?.username || ''), snapshot })
      });
      if (!res.ok) { alert('Signalement impossible, reessayez.'); return; }
      const next = [...reportedConvs, String(convId)];
      setReportedConvs(next);
      try { localStorage.setItem('mjreportedconv', JSON.stringify(next)); } catch {}
    } catch { alert('Signalement impossible, reessayez.'); }
  };
  const fetchConvs = async () => {
    const res = await fetch('/api/messages/conversations', { headers: { Authorization: 'Bearer ' + token } });
    const data = await res.json();
    if (Array.isArray(data)) setConvs(data);
  };

  const fetchMessages = async (convId) => {
    const res = await fetch('/api/messages/conversations/' + convId + '/messages', { headers: { Authorization: 'Bearer ' + token } });
    const data = await res.json();
    if (Array.isArray(data)) setMessages(data);
    fetchConvs();
  };

  const handleSend = async () => {
    if (!text.trim() && !file) return;
    setLoading(true);
    const fd = new FormData();
    if (text) fd.append('text', text);
    if (file) fd.append('image', file);
    const res = await fetch('/api/messages/conversations/' + (activeConv._id || activeConv.id) + '/messages', {
      method: 'POST',
      headers: { Authorization: 'Bearer ' + token },
      body: fd
    });
    if (res.ok) {
      const msg = await res.json();
      setMessages(prev => [...prev, msg]);
      // Notifier le destinataire via signal server
      if (signalRef.current && otherActive) {
        signalRef.current.emit('new-message', {
          toUserId: otherActive._id,
          fromUserId: currentUser._id || currentUser.id,
          fromPseudo: currentUser.username || 'Anonyme',
          preview: text ? text.slice(0, 60) : '📷 Image'
        });
      }
      setText('');
      setFile(null);
      if (fileRef.current) fileRef.current.value = '';
      fetchConvs();
    }
    setLoading(false);
  };

  const getInitiales = (u) => (u?.username || 'U').slice(0,2).toUpperCase();
  const getUnread = (conv) => conv.unreadCount?.[currentUser?._id || currentUser?.id] || 0;

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '260px 1fr', gridTemplateRows: '520px', height: 520, border: '1px solid #e8d0d0', borderRadius: 10, overflow: 'hidden', background: '#fff' }}>
      <div style={{ borderRight: '1px solid #e8d0d0', display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
        <div style={{ padding: '14px 16px', borderBottom: '1px solid #e8d0d0', fontFamily: 'Georgia,serif', fontSize: 14, color: '#8b1a2a' }}>Messages</div>
        <div style={{ overflowY: 'auto', flex: 1 }}>
          {convs.length === 0 && (
            <div style={{ padding: 20, fontSize: 12, color: '#9a8080', fontStyle: 'italic', textAlign: 'center' }}>Aucune conversation</div>
          )}
          {convs.map(conv => {
            const other = getOther(conv);
            const unread = getUnread(conv);
            return (
              <div key={conv._id} onClick={() => setActiveConv(conv)}
                style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', cursor: 'pointer', borderBottom: '1px solid #f5eeeb', background: activeConv?._id === conv._id || activeConv?.id === conv._id || activeConv?._id === conv.id ? '#f5eeeb' : '#fff' }}
                onMouseEnter={e => { if (activeConv?._id !== conv._id) e.currentTarget.style.background = '#fafaf8'; }}
                onMouseLeave={e => { if (activeConv?._id !== conv._id) e.currentTarget.style.background = '#fff'; }}>
                <div style={{ width: 40, height: 40, borderRadius: '50%', background: '#e8c0c8', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, color: '#8b1a2a', flexShrink: 0, overflow: 'hidden' }}>
                  {other?.photo ? <img src={other.photo} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : getInitiales(other)}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#1a1010', marginBottom: 2 }}>{other?.username}</div>
                  <div style={{ fontSize: 12, color: '#9a8080', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{conv.lastMessage || '...'}</div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4, flexShrink: 0 }}>
                  <div style={{ fontSize: 12, color: '#9a8080' }}>{timeAgo(conv.lastMessageAt)}</div>
                  {unread > 0 && <div style={{ background: '#8b1a2a', color: '#fff', borderRadius: '50%', width: 16, height: 16, fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 }}>{unread}</div>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {activeConv ? (
        <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
          <div style={{ padding: '12px 16px', borderBottom: '1px solid #e8d0d0', display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#e8c0c8', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: '#8b1a2a', overflow: 'hidden' }}>
              {otherActive?.photo ? <img src={otherActive.photo} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : getInitiales(otherActive)}
            </div>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#1a1010', flex: 1 }}>{otherActive?.username}</div>
            {reportedConvs.includes(String(activeConv?._id || activeConv?.id)) ? (
              <span style={{ fontSize: 12, color: '#9a8080', display: 'flex', alignItems: 'center', gap: 4, border: '1px solid #e0d0d0', borderRadius: 8, padding: '5px 9px' }}>{'\u2713'} Signale</span>
            ) : (
              <button onClick={handleSignalerConv} title="Signaler cette discussion" style={{ background: 'none', border: '1px solid #d8b0b0', borderRadius: 8, color: '#9a6060', fontSize: 12, padding: '5px 9px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>{'\u2691'} Signaler</button>
            )}
            <BoutonAppelVideo onClick={visio.visioMode ? visio.stopVisio : visio.startVisio} actif={visio.visioMode} disabled={!otherActive?._id} />
          </div>

          {visio.visioMode && (
            <div style={{ padding: '10px 16px', background: '#1a1010', textAlign: 'center' }}>
              <video ref={visio.remoteVideoRef} autoPlay playsInline style={{ width: '100%', borderRadius: 8, background: '#000', maxHeight: 220 }} />
              <video ref={visio.localVideoRef} autoPlay playsInline muted style={{ width: 90, borderRadius: 6, position: 'relative', top: -70, left: 6, border: '2px solid #fff' }} />
              <div style={{ color: '#fff', fontSize: 12, marginTop: -8 }}>{visio.visioStatus} {visio.callTime > 0 ? '· ' + visio.formatTime(visio.callTime) : ''}</div>
            </div>
          )}

          <div style={{ flex: 1, overflowY: 'auto', padding: 16, display: 'flex', flexDirection: 'column', gap: 10, background: '#fafaf8' }}>
            {messages.map((msg, i) => {
              const isMe = msg.sender?._id === currentUser?._id || msg.sender?._id === currentUser?.id;
              return (
                <div key={i} style={{ display: 'flex', gap: 8, maxWidth: '75%', alignSelf: isMe ? 'flex-end' : 'flex-start', flexDirection: isMe ? 'row-reverse' : 'row' }}>
                  <div style={{ width: 28, height: 28, borderRadius: '50%', background: isMe ? '#e8c0c8' : '#a8c8d8', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: isMe ? '#8b1a2a' : '#1a4a5a', flexShrink: 0, overflow: 'hidden' }}>
                    {msg.sender?.photo ? <img src={msg.sender.photo} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : getInitiales(msg.sender)}
                  </div>
                  <div>
                    {msg.text && <div style={{ background: isMe ? '#8b1a2a' : '#fff', color: isMe ? '#fff' : '#1a1010', border: isMe ? 'none' : '1px solid #e8d0d0', borderRadius: 12, padding: '8px 12px', fontSize: 12, lineHeight: 1.5 }}>{msg.text}</div>}
                    {msg.imageUrl && <img src={msg.imageUrl} alt="" style={{ maxWidth: 200, borderRadius: 10, display: 'block', marginTop: msg.text ? 4 : 0 }} />}
                    <div style={{ fontSize: 12, color: '#9a8080', marginTop: 2, textAlign: isMe ? 'right' : 'left' }}>{timeAgo(msg.createdAt)}</div>
                  </div>
                </div>
              );
            })}
            <div ref={bottomRef} />
          </div>

          <div style={{ padding: '12px 16px', borderTop: '1px solid #e8d0d0', display: 'flex', gap: 8, alignItems: 'center', background: '#fff' }}>
            <button onClick={() => fileRef.current?.click()} style={{ background: 'none', border: 'none', color: '#8b1a2a', fontSize: 18, cursor: 'pointer' }}>📷</button>
            <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={e => setFile(e.target.files[0])} />
            {file && <span style={{ fontSize: 12, color: '#8b1a2a' }}>📎</span>}
            <input value={text} onChange={e => setText(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSend()}
              placeholder="Ecrire un message..."
              style={{ flex: 1, padding: '9px 14px', border: '1px solid #c49090', borderRadius: 20, fontSize: 13, background: '#fafaf8' }} />
            <EmojiPicker onEmojiClick={emoji => setText(prev => prev + emoji)} />
            <button onClick={handleSend} disabled={loading || (!text.trim() && !file)}
              style={{ padding: '9px 18px', borderRadius: 20, border: 'none', background: loading ? '#c49090' : '#8b1a2a', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
              →
            </button>
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9a8080', fontSize: 13, fontStyle: 'italic' }}>
          Selectionnez une conversation
        </div>
      )}
    </div>
  );
}
